import {
    isBlank,
    hasText,
    isEmptyMenu,
    isEmptyActividad,
    isEmptyParticipacion,
    isEmptyCaceria,
} from "./eventoUtils";

/**
 * Construye el payload final que espera tu backend.
 * - Filtra menús / actividades vacías
 * - Remapea índices de menús (0-based) a menusIds (1-based)
 * - Recalcula actividadIds por persona (1-based)
 * - Filtra cacerías vacías y normaliza participaciones internas
 */
export function buildEventoPayload({
    form,
    uploadsCacerias = [],
    getRolNameById, // fn(rolId) => "CAZADOR" | "SECRETARIO" | ...
}) {
    // 1) MENÚS: quitar vacíos + mapa oldIndex -> newIndex
    const rawMenus = Array.isArray(form.menus) ? form.menus : [];

    const menusKeep = rawMenus
        .map((m) => ({
            nombre: m?.nombre ?? "",
            fecha: m?.fecha ?? "",
            lugar: m?.lugar ?? "",
            comensales: m?.comensales ?? 0,
            platoIds: Array.isArray(m?.platoIds) ? m.platoIds : [],
            vinoIds: Array.isArray(m?.vinoIds) ? m.vinoIds : [],
        }))
        .filter((m) => !isEmptyMenu(m));

    const menuIndexMap = new Map(); // old -> new (0-based)
    let runningMenuIdx = 0;
    rawMenus.forEach((m, oldIdx) => {
        const candidate = {
            nombre: m?.nombre ?? "",
            fecha: m?.fecha ?? "",
            lugar: m?.lugar ?? "",
            comensales: m?.comensales ?? 0,
            platoIds: Array.isArray(m?.platoIds) ? m.platoIds : [],
            vinoIds: Array.isArray(m?.vinoIds) ? m.vinoIds : [],
        };
        if (!isEmptyMenu(candidate)) {
            menuIndexMap.set(oldIdx, runningMenuIdx);
            runningMenuIdx += 1;
        }
    });

    // 2) ACTIVIDADES: quitar vacías
    const rawActs = Array.isArray(form.actividades) ? form.actividades : [];
    const actsKeep = rawActs
        .map((a) => ({
            nombre: a?.nombre ?? "",
            tipo: a?.tipo ?? "",
            fecha: a?.fecha ?? "",
            participanteIds: Array.isArray(a?.participanteIds) ? a.participanteIds : [],
        }))
        .filter((a) => !isEmptyActividad(a));

    // 3) PARTICIPACIONES EVENTO: quitar sin persona + remap menuIds (0-based)
    const rawParts = Array.isArray(form.participaciones) ? form.participaciones : [];
    const participacionesKeep = rawParts
        .filter((p) => !isEmptyParticipacion(p))
        .map((p) => ({
            id: p.id ?? null, // <- CLAVE
            personaId: p.personaId ? parseInt(p.personaId) : null,

            orden: p.orden ?? null, // <- AÑADIR ESTO

            vinoIds: Array.isArray(p.vinoIds) ? p.vinoIds.filter((id) => id != null).map(Number) : [],
            habitacionIds: Array.isArray(p.habitacionIds) ? p.habitacionIds.filter((id) => id != null).map(Number) : [],

            menuIds: (Array.isArray(p.menuIds) ? p.menuIds : [])
                .map((oldIdx) => menuIndexMap.get(oldIdx))
                .filter((v) => v != null),
        }));



    // 4) Recalcular actividadIds por persona (1-based) a partir de actsKeep.participanteIds
    const actividadesByPersonaNew = new Map(); // personaId -> Set(newActIdx1)
    actsKeep.forEach((act, newIdx0) => {
        const newIdx1 = newIdx0 + 1;
        (act.participanteIds || []).forEach((pid) => {
            const set = actividadesByPersonaNew.get(pid) || new Set();
            set.add(newIdx1);
            actividadesByPersonaNew.set(pid, set);
        });
    });

    const participacionesLimpias = [...participacionesKeep]
        .sort((a, b) => {
            const oa = a.orden ?? 999999;
            const ob = b.orden ?? 999999;
            if (oa !== ob) return oa - ob;
            return (a.id ?? a.personaId ?? 0) - (b.id ?? b.personaId ?? 0);
        })
        .map((p, index) => ({
            ...p,
            orden: p.orden ?? index,
            actividadIds: Array.from(actividadesByPersonaNew.get(p.personaId) || []),
            menusIds: (p.menuIds || []).map((idx0) => idx0 + 1),
        }));



    // 5) CACERÍAS: filtrar vacías + normalizar participaciones internas
    const caceriasKeep = (Array.isArray(form.cacerias) ? form.cacerias : [])
        .map((c, cIdx) => {
            const partClean = (Array.isArray(c.participaciones) ? c.participaciones : [])
                .filter((p) => !isBlank(p?.personaId) && !isBlank(p?.rolId))
                .map((p) => {
                    const rolName = getRolNameById?.(p.rolId);
                    const esCaz = rolName === "CAZADOR";

                    return {
                        personaId: p.personaId ? parseInt(p.personaId) : null,
                        rolId: p.rolId ? parseInt(p.rolId) : null,
                        arma1Id: p.arma1Id ? parseInt(p.arma1Id) : null,
                        arma2Id: p.arma2Id ? parseInt(p.arma2Id) : null,
                        secretarioId: esCaz && p.secretarioId ? parseInt(p.secretarioId) : null,
                        cargadorId: esCaz && p.cargadorId ? parseInt(p.cargadorId) : null,
                    };
                });

            const rutaDocumento =
                uploadsCacerias?.[cIdx]?.url ?? (hasText(c.rutaDocumento) ? c.rutaDocumento : null);

            const enlaces = (Array.isArray(c.enlaces) ? c.enlaces : []).map((s) => String(s).trim()).filter(Boolean);

            return {
                fecha: c?.fecha || "",
                nombre: hasText(c?.nombre) ? c.nombre.trim() : null,
                tipoCaceria: c?.tipoCaceria || "",
                rutaDocumento,
                enlaces,
                participaciones: partClean,
            };
        })
        .filter((c) => !isEmptyCaceria(c));

    // 6) Payload final
    const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        esProvisional: form.esProvisional,
        transfer: form.transfer,
        enlaces: (Array.isArray(form.enlaces) ? form.enlaces : []).map((s) => String(s).trim()).filter(Boolean),
        agenteIds: Array.isArray(form.agenteIds) ? form.agenteIds : [],
        actividades: actsKeep.map((a) => ({ nombre: a.nombre, tipo: a.tipo, fecha: a.fecha })),
        menus: menusKeep.map((m) => ({
            nombre: m.nombre,
            fecha: m.fecha,
            lugar: m.lugar,
            comensales:
                typeof m.comensales === "number" && !Number.isNaN(m.comensales)
                    ? m.comensales
                    : participacionesLimpias.length,
            platoIds: Array.isArray(m.platoIds) ? m.platoIds : [],
            vinoIds: Array.isArray(m.vinoIds) ? m.vinoIds : [],
        })),
        participaciones: participacionesLimpias,
        cacerias: caceriasKeep,
    };

    return payload;
}
